import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation } from '@apollo/client'
import { DocletContent } from '../../../doclets/types'
import {
  EditorPerson,
  getDraftConflictVersion,
  isNotFoundError,
  saveShareDocletDraftMutation
} from '../api'
import { classifyRemoteVersion } from './classifyRemoteVersion'

export type SaveStatus =
  | 'saved'
  | 'pending'
  | 'saving'
  | 'error'
  | 'conflict'
  // Someone deleted the doclet: there's nothing left to save to
  | 'deleted'

export const AUTOSAVE_DELAY_MS = 1000
const RETRY_DELAY_MS = 5000

interface AutosaveOptions {
  docletId: string
  content: DocletContent
  /** The draft as loaded from the API: the baseline nothing needs saving against. */
  loadedContent: DocletContent
  loadedDraftVersion: number
}

export interface RemoteSave {
  draftVersion: number
  /** Who saved it, when known. */
  by: EditorPerson | null
}

/**
 * Saves the draft AUTOSAVE_DELAY_MS after edits stop, one save at a time.
 *
 * Every save sends the draftVersion it's based on, and the API rejects it if
 * someone else has saved since. Autosave then stops in 'conflict' until the
 * editor chooses to overwrite (resave on top of their version) or reload.
 *
 * reportRemoteVersion lets the builder pause *before* that happens, when it
 * hears about another editor's save (the shareDocletUpdated subscription, or
 * a version check on focus / reconnect). That's the early warning; the API's
 * version check is the guarantee, so a missed event only means finding out on
 * the next save instead.
 *
 * Content is compared by identity: the builder reducer returns a new content
 * object only when content actually changes (selecting a block doesn't).
 */
const useDocletAutosave = ({
  docletId,
  content,
  loadedContent,
  loadedDraftVersion
}: AutosaveOptions) => {
  const [save] = useMutation(saveShareDocletDraftMutation)
  const [status, setStatus] = useState<SaveStatus>('saved')
  // The other editor's save that put this builder in conflict
  const [conflict, setConflict] = useState<RemoteSave | null>(null)

  const baseVersion = useRef(loadedDraftVersion)
  const lastSaved = useRef(loadedContent)
  const latest = useRef(content)
  const inFlight = useRef(false)
  const inConflict = useRef(false)
  const deleted = useRef(false)
  // A remote version seen while a save was in flight, judged when it finishes
  const deferredRemote = useRef<RemoteSave | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mounted = useRef(true)
  useEffect(
    () => () => {
      mounted.current = false
    },
    []
  )

  latest.current = content

  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
  }

  // A final save can still be finishing after the builder unmounts
  const safeSetStatus = (next: SaveStatus) => {
    if (mounted.current) setStatus(next)
  }

  const enterConflict = (remote: RemoteSave) => {
    clearTimer()
    inConflict.current = true
    if (!mounted.current) return
    // Keep the name if a later report of the same conflict doesn't have one
    setConflict(current =>
      current && current.draftVersion === remote.draftVersion && !remote.by
        ? current
        : remote
    )
    setStatus('conflict')
  }

  const judgeRemote = (remote: RemoteSave) => {
    const verdict = classifyRemoteVersion({
      remoteVersion: remote.draftVersion,
      knownVersion: baseVersion.current,
      saveInFlight: inFlight.current
    })
    if (verdict === 'defer') {
      if (
        !deferredRemote.current ||
        remote.draftVersion > deferredRemote.current.draftVersion
      ) {
        deferredRemote.current = remote
      }
    } else if (verdict === 'conflict') {
      enterConflict(remote)
    }
  }

  const flush = useCallback(
    async (overwriteVersion?: number) => {
      clearTimer()
      const overwriting = overwriteVersion !== undefined
      if (
        deleted.current ||
        inFlight.current ||
        (inConflict.current && !overwriting)
      ) {
        return
      }

      const toSave = latest.current
      if (toSave === lastSaved.current && !overwriting) {
        safeSetStatus('saved')
        return
      }

      inFlight.current = true
      safeSetStatus('saving')
      try {
        const { data } = await save({
          variables: {
            id: docletId,
            content: toSave,
            baseDraftVersion: overwriting
              ? overwriteVersion
              : baseVersion.current
          }
        })
        baseVersion.current = data.saveShareDocletDraft.draftVersion
        lastSaved.current = toSave
        inConflict.current = false
        if (mounted.current) setConflict(null)
        inFlight.current = false

        const remote = deferredRemote.current
        deferredRemote.current = null
        if (remote) {
          judgeRemote(remote)
          if (inConflict.current) return
        }

        if (latest.current !== toSave) {
          // Edits arrived while saving; save those too
          safeSetStatus('pending')
          timer.current = setTimeout(() => flush(), AUTOSAVE_DELAY_MS)
        } else {
          safeSetStatus('saved')
        }
      } catch (error) {
        inFlight.current = false
        const currentVersion = getDraftConflictVersion(error)
        if (currentVersion !== null) {
          const remote = deferredRemote.current
          deferredRemote.current = null
          enterConflict({
            draftVersion: currentVersion,
            by: remote?.draftVersion === currentVersion ? remote.by : null
          })
        } else if (isNotFoundError(error)) {
          // Deleted (by someone else, or in another tab): stop saving
          deleted.current = true
          safeSetStatus('deleted')
        } else {
          safeSetStatus('error')
          timer.current = setTimeout(() => flush(), RETRY_DELAY_MS)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [docletId, save]
  )

  useEffect(() => {
    if (
      content === lastSaved.current ||
      inConflict.current ||
      deleted.current
    ) {
      return
    }
    setStatus(current => (current === 'saving' ? current : 'pending'))
    if (inFlight.current) return // flush picks the change up when it finishes
    clearTimer()
    timer.current = setTimeout(() => flush(), AUTOSAVE_DELAY_MS)
  }, [content, flush])

  // Save anything outstanding when the builder closes
  useEffect(
    () => () => {
      if (timer.current && !inConflict.current) {
        clearTimer()
        flush()
      }
    },
    [flush]
  )

  /** A draftVersion seen on the server: another editor's save, or our own. */
  const reportRemoteVersion = useCallback(
    (remote: RemoteSave) => judgeRemote(remote),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  /** Keep this editor's version, saved on top of the other editor's. */
  const overwrite = useCallback(() => {
    if (conflict) flush(conflict.draftVersion)
  }, [conflict, flush])

  return {
    status,
    conflict,
    overwrite,
    reportRemoteVersion,
    hasUnsavedChanges: status !== 'saved',
    /** The draftVersion of the last save, which publishing must name */
    getDraftVersion: () => baseVersion.current
  }
}

export default useDocletAutosave
